<template>
    <div>

        <rkm-area
            type="fieldset"
            title="Endereço"
        >

            <rkm-row v-if="data.form">
                <rkm-col cols="3">
                    <rkm-form-select
                        :options="data.form.logradouroTipos"
                        textField="descricao"
                        valueField="id"
                        label="Tipo de logradouro"
                        v-model="data.model.endereco.logradouroTipoId"
                        :disabled="isDisabled()"
                        show-default-option
                        @change="setFields('logradouroTipoId')"
                        :validations="validations.logradouroTipo"
                    />
                </rkm-col>

                <rkm-col cols="5">
                    <rkm-form-input-autocomplete
                        :searchFields="localForm.autocompleteFields.logradouro"
                        :requestQueryStringParams="{ tipo_id: data.model.endereco.logradouroTipoId }"
                        :repository="repositories.logradouro"
                        v-model="data.model.endereco.logradouro"
                        textField="nome"
                        :validations="validations.logradouro"
                        label="Logradouro"
                        uppercase
                        :disabled="isDisabled()"
                    />
                </rkm-col>

                <rkm-col cols="4">
                    <rkm-form-select
                        :options="data.form.bairros"
                        textField="nome"
                        valueField="id"
                        label="Bairro"
                        v-model="data.model.endereco.bairroId"
                        :validations="validations.bairroId"
                        disabled
                        default-option-text="Todos"
                        show-default-option
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-input
                        v-model="data.model.endereco.logradouroCep"
                        :validations="validations.logradouroCep"
                        disabled
                        v-mask="mask.cep"
                        label="CEP"
                        validation-mode="lazy-change"
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-input
                        :disabled="isDisabled() || data.model.endereco.semNumero || hasLogradouro()"
                        label="Número"
                        v-model="data.model.endereco.numero"
                        :validations="validations.numero"
                        number
                    >
                        <rkm-input-group-append slot="append">
                            <rkm-input-group-text>
                                <rkm-form-checkbox
                                    inline
                                    v-model="data.model.endereco.semNumero"
                                    :disabled="isDisabled() || hasLogradouro()"
                                >
                                    Sem número
                                </rkm-form-checkbox>
                            </rkm-input-group-text>
                        </rkm-input-group-append>
                    </rkm-form-input>
                </rkm-col>

                <rkm-col cols="2">
                    <rkm-form-input-autocomplete
                        :disabled="hasLogradouro()"
                        :searchFields="localForm.autocompleteFields.loteamento"
                        :repository="repositories.loteamento"
                        v-model="data.model.endereco.loteamento"
                        :validations="validations.loteamento"
                        label="Loteamento"
                        uppercase
                    >
                        <rkm-input-group-append slot="append">
                            <rkm-button
                                :disabled="hasLogradouro()"
                                @click="$refs.ModalLoteamento.open()"
                                color="primary"
                                title="Cadastrar loteamento"
                            >
                                <rkm-icon icon="plus" />
                            </rkm-button>
                        </rkm-input-group-append>
                    </rkm-form-input-autocomplete>
                </rkm-col>

                <rkm-col cols="4">
                    <rkm-form-input
                        v-model="data.model.endereco.complemento"
                        label="Complemento"
                        :disabled="isDisabled() || hasLogradouro()"
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-input
                        :disabled="hasLogradouro()"
                        v-model="data.model.endereco.latitude"
                        :validations="validations.latitude"
                        label="Latitude"
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-input
                        :disabled="hasLogradouro()"
                        v-model="data.model.endereco.longitude"
                        :validations="validations.longitude"
                        label="Longitude"
                    />
                </rkm-col>

                <rkm-col cols="6">
                    <rkm-form-select
                        :disabled="data.model.endereco.foraArea || hasLogradouro()"
                        :options="data.form.microareas"
                        label="Área/Microárea do domicílio"
                        show-default-option
                        text-field="descricao"
                        v-model="data.model.endereco.microareaId"
                        default-option-text="Selecione"
                        value-field="id"
                        class="pt-0"
                        :validations="validations.microareas"
                        @change="handleCadastro"
                    >
                        <rkm-input-group-append slot="append">
                            <rkm-input-group-text>
                                <rkm-form-checkbox
                                    v-model="data.model.endereco.foraArea"
                                    @change="handleCadastro"
                                    :disabled="hasLogradouro()"
                                >
                                    Fora de área
                                </rkm-form-checkbox>
                            </rkm-input-group-text>
                        </rkm-input-group-append>
                    </rkm-form-select>
                </rkm-col>
            </rkm-row>
        </rkm-area>

        <ModalLoteamento
            ref="ModalLoteamento"
            @save="data.model.endereco.loteamento = $event"
        />

    </div>

</template>

<script>
// Moment.
import moment from 'moment'

// Vuex.
import { mapActions } from 'vuex'

// Filters
import formats from '@/filters/formats'

// Mixins
import { repository, stringCase, object, mask, unmask, importacaoCns } from '@/mixins'

import ModalLoteamento from '@/components/cadastro/loteamento/Modal'

export default {
    components: {
        ModalLoteamento
    },
    computed: {
        validations() {
            return {
                logradouroCep: {
                    required: true,
                    min_max_without_mask: 8,
                    has_cep: true,
                },
                logradouro: {
                    required: true
                },
                logradouroTipo: {
                    required: true
                },
                bairroId: {
                    required: true
                },
                numero: {
                    required: !this.data.model.endereco.semNumero,
                    max: 6
                },
                complemento: {
                    min: 3,
                    max: 30,
                    regex: this.importacaoCns.regexValidacao,
                    termo_invalido: this.termosInvalidos,
                },
                latitude: {
                    min: 10,
                    max: 10
                },
                longitude: {
                    min: 10,
                    max: 10
                },
                microareas: {
                    required: true
                }
            }
        },
        fields() {
            return {
                autocomplete: {
                    usuario: [
                        { key: "cadastroCidadao.nome", searchable: true },
                        { key: "cadastroUsuario.registroNumero", searchable: true },
                    ],
                    cid: [{ key: "nome", searchable: true }],
                },
            }
        },
    },
    created() {
        this.repositories = {

            usuario: this.getConectaSusRepository('cadastro.sujeitoAtencao.usuario'),
            cadastroDomiciliar: this.getConectaSusRepository('atencaoBasica.cadastroDomiciliar'),
            logradouro: this.getConectaSusRepository('cadastro.localizacao.logradouro'),
            loteamento: this.getConectaSusRepository('cadastro.localizacao.loteamento')
        }
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        getUnidades() {

            this.setLoading()

            this.repositories.cadastroDomiciliar.getUnidades({usuario_id: this.data.model.cadastro.usuario.id})
                .then(({ data }) => {

                    this.unidades = data
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        getEquipes() {

            this.setLoading()

            this.repositories.cadastroDomiciliar.getEquipes({
                usuario_id: this.data.model.cadastro.usuario.id,
                unidade_id: this.data.model.cadastro.unidadeId
            })
                .then(({ data }) => {

                    this.equipes = data
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        getOcupacoes() {

            this.setLoading()

            this.repositories.cadastroDomiciliar.getOcupacoes({
                usuario_id: this.data.model.cadastro.usuario.id,
                unidade_id: this.data.model.cadastro.unidadeId
            })
                .then(({ data }) => {

                    this.ocupacoes = data
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        setCadastro() {

            this.data.model.cadastro.unidadeId = null
            this.data.model.cadastro.equipeId = null
            this.data.model.cadastro.ocupacaoId = null
            this.data.model.cadastro.dataUsuarioResponsavel = moment(this.data.form.dataAtual, 'DD/MM/YYYY').toDate()
        },
        isDisabled() {

            return this.id == null ? false : true
        },
        hasLogradouro() {

            return this.data.model.endereco.logradouro ? false : true
        },
        setFields(field) {

            if(field == 'logradouroTipoId') {

                Object.keys(this.endereco).forEach((key) => {
                    this.data.model.endereco[key] = this.endereco[key];
                });
            }
        },
        loadLogradouro(logradouro) {

            this.data.model.endereco.logradouroCep = this.formatCep(logradouro.cep)

            this.$nextTick(() => this.data.model.endereco.logradouro = logradouro)
        },
        handleCadastro() {

            if (!this.id) {

                this.setLoading()

                this.repositories.cadastroDomiciliar.getCadastro(this.prepareData())
                    .then(({ data }) => {

                        if (typeof data === 'number') {
                            this.showConfirmation(
                                `O cadastro domiciliar para o endereço fornecido (Logradouro, número e complemento) já está
                                registrado. Para prosseguir com o cadastro, você será direcionado ao formulário domiciliar
                                correspondente a esse endereço. Deseja continuar?`,
                                async () => this.$emit('redirect', data),
                                async () => this.$emit('exit')
                            )
                        } else {

                            this.setFieldsDomicilio(data)
                        }
                    })
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            }
        },
        setFieldsDomicilio(data) {

            const dados = data.informacaoResidencial

            if (dados) {

                this.data.model.residencia.imovelTipoId = dados.imovelTipoId ?? null
                this.data.model.residencia.situacaoDomicilioId = dados.situacaoDomicilioId ?? null
                this.data.model.residencia.domicilioTipoId = dados.domicilioTipoId ?? null
                this.data.model.residencia.quantidadeComodos = dados.quantidadeComodos ?? null
                this.data.model.residencia.situacaoRuralId = dados.situacaoRuralId ?? null
                this.data.model.residencia.domicilioZonaId = dados.domicilioZonaId ?? null
                this.data.model.residencia.domicilioTipoAcessoId = dados.domicilioTipoAcessoId ?? null
                this.data.model.residencia.materialPredominanteId = dados.materialPredominanteId ?? null
                this.data.model.residencia.abastecimentoId = dados.abastecimentoId ?? null
                this.data.model.residencia.tratamentoId = dados.tratamentoId ?? null
                this.data.model.residencia.destinoLixoId = dados.destinoLixoId ?? null
                this.data.model.residencia.formaEscoamentoId = dados.formaEscoamentoId ?? null
                this.data.model.residencia.possuiAnimais = dados.possuiAnimais ?? null
                this.data.model.residencia.energiaEletrica = dados.energiaEletrica ?? null
                this.data.model.residencia.quantidadeAnimais = dados.quantidadeAnimais ?? null
                this.data.model.residencia.animal = dados.newAnimal ?? []
                this.data.model.instituicao.nome = dados.instituicaoNome ?? null
                this.data.model.instituicao.outrosProfissionais = dados.instituicaoOutrosProfissionais ?? null
                this.data.model.instituicao.nomeResponsavelTecnico = dados.instituicaoNomeResponsavelTecnico ?? null
                this.data.model.instituicao.cnsResponsavelTecnico = dados.instituicaoCnsResponsavelTecnico ?? null
                this.data.model.instituicao.cargoResponsavelTecnico = dados.instituicaoCargoResponsavelTecnico ?? null
                this.data.model.instituicao.telefoneResponsavelTecnico = dados.instituicaoTelefoneResponsavelTecnico ?? null
                this.data.model.instituicao.escalaCoelhoId = dados.escalaCoelhoId ?? null
                this.data.model.informacao.higieneResidencialId = dados.higieneResidencialId ?? null
                this.data.model.informacao.bolsaFamilia = dados.bolsaFamilia ?? null
                this.data.model.informacao.valeGas = dados.valeGas ?? null
                this.data.model.informacao.bolsaEscola = dados.bolsaEscola ?? null
                this.data.model.informacao.lazer = dados.newLazer ?? []
                this.data.model.informacao.bpc = dados.bpc ?? null
                this.data.model.endereco.foraArea = dados.foraArea ?? null
                this.data.model.endereco.microareaId = dados.microareaId ?? null
            }
        },
        prepareData() {

            const endereco = this.clone(this.data.model.endereco)
            endereco.logradouro_id = endereco.logradouro.id
            endereco.loteamento_id = endereco.loteamento ? endereco.loteamento.id : null

            return this.camelObjectKeysToSnakeRecursive({
                endereco
            })
        },
    },
    data() {
        return {
            unidades: [],
            equipes: [],
            ocupacoes: [],
            disabledUnidade: true,
            model: {
                usuario: null
            },
            localForm: {
                autocompleteFields: {
                    logradouro: [
                        { key: 'tipo.descricao', searchable: true },
                        { key: 'nome', searchable: true },
                    ],
                    loteamento: [
                        { key: 'nome', searchable: true },
                    ],
                },
            },
            endereco: {
                logradouro: null,
                bairroId: null,
                logradouroCep: null,
                numero: null,
                semNumero: null,
                loteamento: null,
                complemento: '',
                latitude: '',
                longitude: '',
                microareaId: null,
            }
        }
    },
    mixins: [ repository, stringCase, object, formats, mask, unmask, importacaoCns ],
    props: {
        data: { type: Object, required: false },
        id: { type: Number }
    },
    watch: {
        'data.model.endereco.semNumero': function (semNumero) {

            if (semNumero) {

                this.data.model.endereco.numero = null
            }
        },
        'data.model.endereco.foraArea': function (foraArea) {

            if (foraArea) {

                this.data.model.endereco.microareaId = null
            }
        },
        'data.model.endereco.logradouro': function (logradouro) {

            this.data.model.endereco.bairroId = null
            this.data.model.endereco.logradouroCep = null
            this.data.model.endereco.numero = null
            this.data.model.endereco.semNumero = null

            if(logradouro) {

                this.data.model.endereco.bairroId = logradouro.bairro.id
                this.data.model.endereco.logradouroCep = logradouro.cep
            }
        },
    },
}
</script>